import { api } from '../config';
import { authFetch } from '../utils/authFetch';

// A projection's excluded row is not a readiness verdict. Request that row
// in a second projection, without inferring eligibility or either role locally.
export async function getComparisonVersions(documentId, viewedVersionId) {
    const list = async examinedId => (await api.get(`documents/${documentId}/versions`, {
        params: { examined_document_version_id: examinedId },
    })).data.data;
    const rows = await list(viewedVersionId);
    const other = rows.find(row => row.document_version_id !== viewedVersionId);
    if (!other) return rows;
    const second = await list(other.document_version_id);
    const viewed = second.find(row => row.document_version_id === viewedVersionId);
    return rows.map(row => row.document_version_id === viewedVersionId && viewed ? viewed : row);
}

export async function importComparisonVersion(documentId, file) {
    const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = () => reject(new Error('Impossible de lire ce fichier.'));
        reader.readAsDataURL(file);
    });
    return (await api.post(`documents/${documentId}/versions`, {
        title: file.name, file_content: fileContent, activate: false,
    })).data.data;
}

// Only the typed versions path uses fetch, to retain HTTP refusal JSON details.
// The historical File transport is unchanged. No reconnect or replay here.
export function openVersionComparisonStream(url, headers, onMessage, onError) {
    const controller = new AbortController();
    let terminal = false;
    const stream = { close: () => controller.abort() };
    const run = async () => {
        const response = await authFetch(url, { headers, signal: controller.signal });
        if (!response.ok) {
            const detail = await response.json().catch(() => ({}));
            throw Object.assign(new Error(`Lancement refusé (${response.status}).`), {
                response: { status: response.status, data: detail }, status: response.status,
            });
        }
        if (!response.body || !response.headers.get('content-type')?.includes('text/event-stream')) {
            throw new Error('Réponse de lancement invalide.');
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let eventName = '';
        let data = [];
        const line = value => {
            if (!value) {
                if (data.length) {
                    const raw = data.join('\n');
                    if (raw !== '[DONE]') {
                        const message = JSON.parse(raw);
                        if (!message.event && eventName) message.event = eventName;
                        terminal = ['analysis_done', 'comparison_terminal', 'commercial_refusal'].includes(message.event);
                        onMessage(message);
                    }
                }
                data = [];
                eventName = '';
            } else if (value.startsWith('data:')) data.push(value.slice(5).replace(/^ /, ''));
            else if (value.startsWith('event:')) eventName = value.slice(6).trim();
        };
        try {
            while (!controller.signal.aborted && !terminal) {
                const { value, done } = await reader.read();
                buffer += decoder.decode(value, { stream: !done });
                let match;
                // Preserve a CR at a chunk boundary until its LF arrives.
                while ((match = /\r\n|\n|\r(?!$)/.exec(buffer))) {
                    line(buffer.slice(0, match.index));
                    buffer = buffer.slice(match.index + match[0].length);
                    if (terminal) break;
                }
                if (done) {
                    if (buffer) line(buffer.replace(/\r$/, ''));
                    line('');
                    break;
                }
            }
            if (!terminal && !controller.signal.aborted) throw new Error('Le flux a été interrompu avant sa confirmation finale.');
        } finally {
            await reader.cancel().catch(() => {});
        }
    };
    run().catch(error => {
        if (!controller.signal.aborted) onError(error);
    });
    return stream;
}
